import React, { createContext, useContext, useState, useCallback } from "react";
import { toast } from "react-toastify";
import { api } from "../api/axios";

// Terminal Context erstellen
const TerminalContext = createContext();

// Custom Hook zum Verwenden des Terminal Context
export const useTerminal = () => useContext(TerminalContext);

const CONNECTED_TERMINAL_STORAGE_KEY = "pickupTerminalId";

// Terminal Provider Component - Verbindung dieses Arbeitsplatzes (Browser) mit einem
// Mollie-Kartenterminal. Lokal pro Browser gespeichert statt am Benutzerkonto, da mehrere
// Terminals demselben (Filial-)Account zugeordnet sein können und gleichzeitig an
// verschiedenen Rechnern im Einsatz sind.
export const TerminalProvider = ({ children }) => {
  const [showTerminalModal, setShowTerminalModal] = useState(false);
  const [connectedTerminal, setConnectedTerminal] = useState(null);
  const [terminals, setTerminals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingTerminals, setLoadingTerminals] = useState(false);

  const openTerminalModal = () => setShowTerminalModal(true);
  const closeTerminalModal = () => setShowTerminalModal(false);

  const loadAvailableTerminals = useCallback(async () => {
    setLoadingTerminals(true);
    try {
      const { data } = await api.get("/orders-v2/pickup/terminals/mine");
      const list = data?.terminals || [];
      setTerminals(list);
      return list;
    } catch (error) {
      console.error("Fehler beim Laden der verfügbaren Terminals:", error);
      toast.error("Fehler beim Laden der verfügbaren Terminals");
      return [];
    } finally {
      setLoadingTerminals(false);
    }
  }, []);

  // Lädt das für diesen Rechner lokal gespeicherte Terminal und gleicht es mit der
  // Filiale ab (z. B. falls es inzwischen deaktiviert oder umbenannt wurde).
  const loadConnectedTerminal = useCallback(async () => {
    const storedTerminalId = localStorage.getItem(CONNECTED_TERMINAL_STORAGE_KEY);
    if (!storedTerminalId) {
      setConnectedTerminal(null);
      return;
    }

    setLoading(true);
    try {
      const available = await loadAvailableTerminals();
      const match = available.find((t) => t.terminal_id === storedTerminalId) || null;
      setConnectedTerminal(match);
      if (!match) {
        localStorage.removeItem(CONNECTED_TERMINAL_STORAGE_KEY);
      }
    } finally {
      setLoading(false);
    }
  }, [loadAvailableTerminals]);

  const connectTerminal = useCallback(
    (terminalId) => {
      const terminal = terminals.find((t) => t.terminal_id === terminalId);
      if (!terminal) {
        toast.error("Ungültiges Terminal");
        return false;
      }
      localStorage.setItem(CONNECTED_TERMINAL_STORAGE_KEY, terminalId);
      setConnectedTerminal(terminal);
      toast.success(`Terminal "${terminal.label}" verbunden`);
      return true;
    },
    [terminals],
  );

  const disconnectTerminal = useCallback(() => {
    localStorage.removeItem(CONNECTED_TERMINAL_STORAGE_KEY);
    setConnectedTerminal(null);
    toast.info("Terminal-Verbindung getrennt");
  }, []);

  const value = {
    showTerminalModal,
    openTerminalModal,
    closeTerminalModal,
    connectedTerminal,
    terminals,
    loading,
    loadingTerminals,
    loadConnectedTerminal,
    loadAvailableTerminals,
    connectTerminal,
    disconnectTerminal,
  };

  return (
    <TerminalContext.Provider value={value}>
      {children}
    </TerminalContext.Provider>
  );
};
